import { AssortmentItemPriorityDTO, MgItemCalculation } from '@retail/my-assortment/types';
import { CalculusCard } from './CalculusCard';
import {
  NotFoundError,
  RetailTanstackQueryLoadingWrapper,
  SideDrawerPaper,
  SuspenseWithSentryErrorBoundary,
} from '@retail/components';
import {
  MgItemInfo,
  SupplierPriorityDialog,
  SupplierPriorityItem,
  SupplierSelectTabs,
} from '@retail/my-assortment/components';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { useFetchMgItemCalculation } from '@retail/my-assortment/data-access';
import { useAssortmentItemsActions } from '@retail/my-assortment/context';
import { useUserBrands } from '@retail/app/stores/selected-context';
import { ItemPriority } from '@retail/products/types';
import { brandLogo } from '@retail/utils';
import { useDisclosure } from '@shared/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface Props {
  assortmentId: number;
  mgItemNumber: number;
  primaryText: string;
  secondaryText?: string;
  mgSupplierItemId: number;
  onSupplierItemChange: (mgSupplierItemId: number | null) => void;
}

export function CalculusCardContainer({
  assortmentId,
  mgItemNumber,
  primaryText,
  secondaryText,
  mgSupplierItemId,
  onSupplierItemChange,
}: Props) {
  const t = useMyAssortmentTFunction();
  const { selectedBrand } = useUserBrands();

  const loadingState = useFetchMgItemCalculation({
    assortmentId,
    mgItemNumber,
    suspense: false,
  });

  const { isOpen: editPrioModal, onOpen: openPrioModal, onClose: closePrioModal } = useDisclosure();

  const { addItemsToAssortment, expireItems, updateItemsIsLoading, expireItemsIsLoading } =
    useAssortmentItemsActions();

  const queryClient = useQueryClient();

  const handleSavePriorities = useCallback(
    async (
      priorities: SupplierPriorityItem[],
      supplierItems: MgItemCalculation['supplierItems'],
      calculusCalculation: MgItemCalculation
    ) => {
      const supplierItemById = new Map(
        supplierItems.map((supplierItem) => [supplierItem.mgSupplierItemId, supplierItem])
      );

      const itemsToPrioritise: AssortmentItemPriorityDTO[] = [];
      const supplierItemIdsToRemove: number[] = [];

      priorities.forEach((priority) => {
        const supplierItem = supplierItemById.get(priority.mgSupplierItemId);
        if (!supplierItem) return;

        if (priority.tier === 'UNPRIORITIZED') {
          if (supplierItem.inAssortment) {
            supplierItemIdsToRemove.push(priority.mgSupplierItemId);
          }
          return;
        }

        itemsToPrioritise.push({
          mgSupplierId: supplierItem.supplierNumber,
          mgItemNumber: calculusCalculation.mgItemNumber,
          priority: priority.tier === 'MAIN' ? ItemPriority.TEN : ItemPriority.ZERO,
        });
      });

      try {
        if (itemsToPrioritise.length) {
          await addItemsToAssortment(itemsToPrioritise);
        }
        if (supplierItemIdsToRemove.length) {
          await expireItems({
            assortmentId,
            supplierItemIds: supplierItemIdsToRemove,
          });
        }
        closePrioModal();

        queryClient.invalidateQueries({ queryKey: ['calculusCard'] });
      } catch {
        // Errors are surfaced via toast inside useAssortmentItemsActions.
      }
    },
    [addItemsToAssortment, assortmentId, expireItems, closePrioModal, queryClient]
  );

  return (
    <SideDrawerPaper>
      <MgItemInfo
        primaryText={primaryText}
        secondaryText={secondaryText}
        mgItemNumber={mgItemNumber}
        closeProductCard={() => onSupplierItemChange(null)}
      />
      <SuspenseWithSentryErrorBoundary>
        <RetailTanstackQueryLoadingWrapper loadingState={loadingState} showReloadButtonOnError>
          {(mgItemCalculationDTO) => {
            const calculusCalculation = MgItemCalculation.fromDTO(mgItemCalculationDTO, {
              name: selectedBrand.name,
              id: selectedBrand.brandId,
              imgUrl: brandLogo(selectedBrand.brandId).logo,
            });

            const supplierItems = calculusCalculation.supplierItems;
            const selectedSupplierItem = supplierItems.find(
              (supplierItem) => supplierItem.mgSupplierItemId === mgSupplierItemId
            );

            return (
              <>
                <SupplierSelectTabs
                  supplierItems={supplierItems}
                  currentMgSupplierItemId={selectedSupplierItem?.mgSupplierItemId}
                  updateSelectedSupplierItem={onSupplierItemChange}
                  openPrioModal={openPrioModal}
                />
                <SupplierPriorityDialog
                  isOpen={editPrioModal}
                  supplierItems={supplierItems}
                  isSaving={updateItemsIsLoading || expireItemsIsLoading}
                  onClose={closePrioModal}
                  onSave={(priorities: SupplierPriorityItem[]) =>
                    handleSavePriorities(priorities, supplierItems, calculusCalculation)
                  }
                />
                {selectedSupplierItem ? (
                  <CalculusCard
                    assortmentId={assortmentId}
                    supplierItem={selectedSupplierItem}
                    mgItemNumber={calculusCalculation.mgItemNumber}
                  />
                ) : (
                  <NotFoundError subtitle={t('myAssortment.actions.suppliersItemInfoNotFound')} />
                )}
              </>
            );
          }}
        </RetailTanstackQueryLoadingWrapper>
      </SuspenseWithSentryErrorBoundary>
    </SideDrawerPaper>
  );
}
